import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { compose } from 'core/libs/recompose';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';

import SmartImage from 'core/components/SmartImage';
import ThemeSwitch from 'core/components/ThemeSwitch';
import CaptionCredits from 'core/components/CaptionCredits';

import { updateBodyAttributes } from 'core/utils/dom';

import styles from './index.styl';


const withFullscreen = (WrappedComponent) => {
  const FullscreenComponent = (props) => {
    const {
      isMobile,
      isDesktop,
      theme: {
        controls: {
          fullscreen: {
            closeIcon: CloseIcon,
            background,
          },
        },
      },
      block,
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isCaptionExtend, setCaptionExtend] = useState(false);
    const [data, setData] = useState(null);
    const url = block?.attributes?.url;

    const handleImageClick = useCallback((d) => {
      setData(d);
      setIsOpen(true);
    }, [setData, setIsOpen]);

    const handleClose = () => {
      setIsOpen(false);
      setData(null);
    };

    const toggleCaption = () => {
      setCaptionExtend(!isCaptionExtend);
    };

    useEffect(() => {
      updateBodyAttributes({ toFullscreen: isOpen });
    }, [isOpen]);

    if (url?.length) {
      return <WrappedComponent {...props} />;
    }

    return (
      <Fragment>
        <WrappedComponent {...props} onImageClick={handleImageClick} />
        {isOpen && (
          <div className={styles.fullscreen}>
            <style jsx>{`
              .${styles.fullscreen}
                background ${background}
            `}</style>
            <div className={styles.image}>
              <SmartImage
                src={data.cover}
                maxWidth='100%'
                placement='contain'
                {...isDesktop && {
                  width: data.cover.width,
                  height: data.cover.height,
                  maxHeight: '100vh',
                }}
                {...isMobile && {
                  width: data.cover.width,
                  maxHeight: '600px',
                  aspectRatio: data.cover.width / data.cover.height,
                }}
              />
            </div>
            <ThemeSwitch name='dark'>
              <div
                className={cx(
                  styles.caption,
                  { [styles.captionHidden]: !isCaptionExtend },
                )}
                {...isMobile && { onClick: toggleCaption }}
              >
                <CaptionCredits
                  caption={data.caption}
                  credits={data.credits}
                  creditsPrefix={data.creditsPrefix}
                />
              </div>
            </ThemeSwitch>
            <div className={styles.icon} onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
        )}
      </Fragment>
    );
  };

  FullscreenComponent.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    isDesktop: PropTypes.bool.isRequired,
    theme: PropTypes.object,
    block: PropTypes.object,
  };

  return FullscreenComponent;
};

const enhance = compose(
  withBreakpoint,
  withTheme,
  withFullscreen
);

export default enhance;
